"use client"

import IconFont from "@repo/ui/iconfont"
import { useMemo, useState } from "react"

import type { AnnouncementItem } from "../../../servers/newscenter/interface"
import { useAnnouncementCarousel, useAnnouncementDetailPath, useScrollHide, useTextOverflow } from "./hooks"
import { saveAnnouncementClosedId } from "./utils/cookie"

import "./AnnouncementBanner.scss"

import { CloseOutlined } from "@ant-design/icons"
import Link from "next/link"
import Marquee from "react-fast-marquee"
import { Alert } from "antd"

interface AnnouncementBannerClientProps {
  announcements: AnnouncementItem[]
}

/**
 * 公告栏客户端交互组件
 * @description 负责客户端交互逻辑：滚动隐藏、轮播、关闭等
 * - 显示/隐藏判断已在服务端完成，避免客户端闪烁
 * - 仅保留纯交互逻辑
 */
export const AnnouncementBannerClient = ({ announcements }: AnnouncementBannerClientProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const hasMultiple = useMemo(() => announcements.length > 1, [announcements])

  // 滚动隐藏逻辑
  const isScrolledDown = useScrollHide(isVisible)

  // 轮播逻辑
  const { currentIndex, isAnimating } = useAnnouncementCarousel(announcements.length, hasMultiple, isVisible)

  // 当前公告和详情路径
  const currentAnnouncement = announcements[currentIndex] ?? announcements[0]
  const detailPath = useAnnouncementDetailPath(currentAnnouncement)

  // 文本溢出检测（仅单条公告时使用）
  const { shouldScroll, measureRef } = useTextOverflow(hasMultiple ? undefined : currentAnnouncement?.title)

  // 处理关闭
  const handleClose = () => {
    const firstAnnouncement = announcements[0]
    const id = firstAnnouncement?.id

    if (id !== undefined && id !== null) {
      saveAnnouncementClosedId(id)
    }

    // 公告栏收起后首屏占位减少，将 Publicity 使用的上边距变量改回 48px
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--announcement-height", "48px")
    }
    setIsVisible(false)
  }

  if (announcements.length === 0 || !isVisible) {
    return null
  }

  return (
    <div className={`announcementBanner ${isScrolledDown ? "scrolledDown" : ""}`}>
      {/* 隐藏的文本测量元素 - 继承实际样式以确保测量准确 */}
      {!hasMultiple && (
        <span
          ref={measureRef}
          className="text"
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "nowrap",
            top: "-9999px",
            left: "-9999px",
            pointerEvents: "none",
          }}
        >
          <span className="title">{currentAnnouncement?.title || ""}</span>
        </span>
      )}
      <Alert
        className="bannerAlert"
        icon={<IconFont type="icon-a-gonggao21" className="bannerIcon" />}
        banner
        action={
          <button type="button" className="closeButton" onClick={handleClose} aria-label="Close announcement">
            <CloseOutlined />
          </button>
        }
        message={
          hasMultiple ? (
            <Link href={detailPath} target="_blank" className="text" prefetch={false}>
              <div className={`text ${isAnimating ? "animating" : ""}`}>
                <span className="title ellipsis">{`${currentAnnouncement?.title || ""}`}</span>
              </div>
            </Link>
          ) : shouldScroll ? (
            <Marquee pauseOnHover gradient={false}>
              <Link href={detailPath} target="_blank" className="text" prefetch={false}>
                <div className="text">
                  <span className="title ellipsis">{`${currentAnnouncement?.title || ""}`}</span>
                </div>
              </Link>
            </Marquee>
          ) : (
            <Link href={detailPath} target="_blank" className="text" prefetch={false}>
              <div className="text">
                <span className="title ellipsis">{`${currentAnnouncement?.title || ""}`}</span>
              </div>
            </Link>
          )
        }
      />
    </div>
  )
}
